import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [step, setStep] = useState(token ? 'confirming' : 'form'); // 'form' | 'confirming' | 'done'

  // Si token présent dans l'URL → confirmer automatiquement
  useEffect(() => {
    if (token && step === 'confirming') {
      handleConfirm();
    }
  }, []);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/delete-confirm?token=${token}`);
      const data = await response.json();

      if (data.success) {
        setStep('done');
        setMessage({ type: 'success', text: 'Votre compte a été supprimé définitivement.' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Lien invalide ou expiré.' });
        setStep('form');
      }
    } catch {
      setMessage({ type: 'error', text: 'Erreur serveur. Réessayez plus tard.' });
      setStep('form');
    } finally {
      setConfirming(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/auth/delete-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await response.json();

      if (data.success) {
        setMessage({
          type: 'success',
          text: 'Si cet email est associé à un compte, un lien de confirmation a été envoyé.'
        });
        setEmail('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Erreur. Réessayez.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Impossible de contacter le serveur.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.accent} />

        <div style={styles.header}>
          <h1 style={styles.title}>🗑️ Supprimer mon compte</h1>
          <p style={styles.subtitle}>RizzingBot — Gestion de votre compte</p>
        </div>

        {message.text && (
          <div style={{
            ...styles.message,
            ...(message.type === 'success' ? styles.messageSuccess : styles.messageError)
          }}>
            {message.type === 'success' ? '✅ ' : '❌ '}{message.text}
          </div>
        )}

        {step === 'form' && (
          <>
            <div style={styles.warningBox}>
              <p style={styles.warningText}>
                ⚠️ Cette action est <strong>irréversible</strong>. Toutes vos données, 
                conversations et crédits seront supprimés définitivement.
              </p>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <label style={styles.label}>VOTRE EMAIL</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                style={styles.input}
                required
                disabled={loading}
              />
              <button type="submit" style={styles.button} disabled={loading || !email.trim()}>
                {loading ? 'Envoi en cours...' : 'Recevoir le lien de confirmation'}
              </button>
            </form>

            <p style={styles.footer}>
              Vous recevrez un email avec un lien de confirmation valable 1 heure.
              <br />
              <a href="/login" style={styles.link}>← Retour à la connexion</a>
            </p>
          </>
        )}

        {step === 'confirming' && (
          <div style={styles.center}>
            <div style={styles.spinner} />
            <p style={styles.subtitle}>Traitement en cours...</p>
          </div>
        )}

        {step === 'done' && (
          <div style={styles.center}>
            <p style={styles.doneText}>
              Votre compte a été supprimé. Nous sommes désolés de vous voir partir.
            </p>
            <button onClick={() => navigate('/login')} style={styles.button}>
              Retour à l'accueil
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'system-ui, sans-serif' },
  card: { backgroundColor: 'rgba(30,41,59,0.9)', border: '1px solid rgba(148,163,184,0.1)', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '420px', position: 'relative' },
  accent: { position: 'absolute', top: 0, left: 0, right: 0, height: '1px', backgroundColor: 'rgba(239,68,68,0.5)', borderRadius: '16px 16px 0 0' },
  header: { textAlign: 'center', marginBottom: '24px' },
  title: { color: '#f8fafc', fontSize: '22px', fontWeight: '800', margin: '0 0 6px' },
  subtitle: { color: '#64748b', fontSize: '14px', margin: 0 },
  message: { padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', fontWeight: '600', border: '1px solid' },
  messageSuccess: { backgroundColor: 'rgba(16,185,129,0.1)', borderColor: 'rgba(16,185,129,0.3)', color: '#6ee7b7' },
  messageError: { backgroundColor: 'rgba(239,68,68,0.1)', borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5' },
  warningBox: { backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '14px', marginBottom: '20px' },
  warningText: { color: '#fca5a5', fontSize: '13px', lineHeight: '1.6', margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  label: { color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase' },
  input: { padding: '12px 16px', backgroundColor: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '14px', outline: 'none' },
  button: { padding: '13px', backgroundColor: '#dc2626', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', marginTop: '4px' },
  footer: { color: '#64748b', fontSize: '12px', textAlign: 'center', marginTop: '20px', lineHeight: '1.8' },
  link: { color: '#93c5fd', fontWeight: '600', textDecoration: 'none' },
  center: { textAlign: 'center', padding: '20px 0' },
  spinner: { width: '32px', height: '32px', border: '2px solid rgba(148,163,184,0.15)', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' },
  doneText: { color: '#94a3b8', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' },
};

export default DeleteAccount;